import React, { useEffect, useState } from "react";
import CusPageHeader from "components/Molecules/header";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  FormInstance,
  message,
  notification,
  Row,
  Space,
  Typography,
} from "antd";
import Page from "components/Molecules/page";
import styled from "@emotion/styled";
import { ReviewerUploadDoc } from "components/Organisms/uploads/reviewer-upload-doc";
import { useTaskCompliance, useUpdateTaskCompliance } from "./api";
import { useDebounce } from "utils";
import { useProductDetail } from "../Detail/api";
import { useHttp } from "utils/http";
import { ReviewerComplianceCheck } from "components/Organisms/reviewer-compliance-check";
import CreateTestLine from "components/Templates/create-test-line";
import SimpleModal from "components/Molecules/modal/simple-modal";
import useNotifySavingBeforeExit from "utils/use-notify-saving";
import { ModalAlertChangeConformity } from "./components";
import { ModalComplianceExpiryDate } from "../../components/modal-compliance-expiry-date";
import moment from "moment";
import { getDemoFile } from "pages/Reviews/constants";
import { StaticDataContext } from "context/static-data-context";
import { ModalComplianceReject } from "pages/Reviews/components";
import { MainLayout } from "components/Organisms/layouts";
import { useFetchSubCategoriesAndProductGroup } from "pages/Merchant/UploadDocument/api";
import { EditProductModal } from "pages/Merchant/components/edit-product-modal";
import { useEditProduct } from "pages/Merchant/api";
import * as _ from "lodash";
import { ComplianceCheckForm } from "constants/compliance";
import { useGenerateCertPdf } from "pages/Reviews/api";
import merge from "lodash/merge";
import { GENERATION_FIELDS, TEST_LINE_FIELDS } from "./constants";
export function ComplianceCheck() {
  let navigate = useNavigate();
  const typeFormTestLine = React.useRef("DOC");
  const {
    ageGrade,
    serviceRequired,
    testItems: testItemOptions,
    citations: citationOptions,
    legislations: legislationOptions,
  } = React.useContext(StaticDataContext);
  const { search } = useLocation();
  const [isReady, setReady] = React.useState(false);
  const isEditing = React.useRef(false);
  const addTestLineFormRef = React.useRef<{ form: FormInstance }>(null);
  const [form] = Form.useForm();
  const { taskId, id } = useParams();
  const [param] = useState({ taskId, id });
  const {
    mutate: editProductMutation,
    isLoading: isUpdating,
    isError,
    isSuccess,
  } = useEditProduct(id as string);
  const [readOnly, setReadOnly] = useState(true);
  const [visibleModalTestLine, setVisibleModalTestLine] = useState(false);
  const [testLine, setTestLine] = useState({ data: {}, index: -1 });

  const [
    visibleComplianceExpiryDate,
    setVisibleComplianceExpiryDate,
  ] = useState(false);
  const [visibleComplianceReject, setVisibleComplianceReject] = useState(false);
  const [initialValues, setInitialValues] = React.useState<any>({});
  const [editProductModal, setEditProductModal] = React.useState({
    open: false,
    initialValues: {},
  });
  const [renderModal, showPrevent] = useNotifySavingBeforeExit({
    onLeave: () => {
      navigate(-1);
    },
  });
  const [visibleConfirmChange, setVisibleConfirmChange] = useState(false);
  const client = useHttp();
  const { isLoading, error, data: complianceData, retry } = useTaskCompliance(
    useDebounce(param, 200)
  );
  const { data: productDetails, retry: productReload } = useProductDetail(
    useDebounce(param, 200)
  );

  const { mutate, isLoading: isUpdateLoading } = useUpdateTaskCompliance();

  const [
    productCategories,
    subCategories,
    productGroups,
    exportMarkets,
  ] = useFetchSubCategoriesAndProductGroup(
    productDetails?.category ?? "",
    productDetails?.subcategory ?? ""
  );

  const { generate, transformParams } = useGenerateCertPdf();
  const mergedValue = (value: any, defaultValue: any) => {
    if (!value.length) {
      return merge(defaultValue, value || []);
    }
    return value.reduce((acc, current) => {
      const existed = acc.findIndex(({ title }) => {
        return title === current.title;
      });
      if (existed > -1) {
        acc[existed] = merge(acc[existed], current);
      } else {
        acc.push(current);
      }
      return acc;
    }, defaultValue);
  };

  const handleGeneratePDF = (conformityType: string, defaultValue) => {
    const data = form.getFieldValue([
      "testLines",
      conformityType === "DOC"
        ? "docGeneralInformations"
        : "cpcGeneralInformations",
    ]);
    const testLines = form.getFieldValue([
      "testLines",
      conformityType === "DOC" ? "docTestLines" : "cpcTestLines",
    ]);
    const additionalInformation = form.getFieldValue([
      "testLines",
      conformityType === "DOC"
        ? "docAdditionalInformation"
        : "cpcAdditionalInformation",
    ]);

    generate(
      transformParams(
        mergedValue(data, defaultValue),
        {
          additionalInformation: additionalInformation || "",
          testLines,
          productPhotos: initialValues?.product_image
            .map((item: any) => item.url)
            .filter((url) => !!url),
        },
        conformityType as any
      ),
      conformityType as any
    );
  };

  const handleNewData = async () => {
    const product_image = await Promise.all(
      complianceData?.productImages.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          docType: item.type || item.docType,
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const packaging_image = await Promise.all(
      complianceData?.packagingImages.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          docType: item.type || item.docType,
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const test_report = await Promise.all(
      complianceData?.testReport.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          docType: item.type || item.docType,
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const circuit_diagram = await Promise.all(
      complianceData?.circuitDiagram.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          docType: item.type || item.docType,
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const instruction_manual = await Promise.all(
      complianceData?.instructionManual.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          docType: item.type || item.docType,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const safety_data_sheet = await Promise.all(
      complianceData?.safetyDataSheet.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          docType: item.type || item.docType,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const ingredient_list = await Promise.all(
      complianceData?.ingredientList.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          docType: item.type || item.docType,
          url: data.url,
          status: "done",
        };
      })
    );

    const declaration_letter = await Promise.all(
      complianceData?.declarationLetter.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          docType: item.type || item.docType,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const test_plan = await Promise.all(
      complianceData?.testPlan.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          docType: item.type || item.docType,
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );
    const conformity_certificate = await Promise.all(
      (complianceData?.conformityCertificate ?? []).map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          docType: item.type || item.docType,
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );
    const values = {
      packaging_image,
      product_image,
      test_report,
      circuit_diagram,
      instruction_manual,
      safety_data_sheet,
      ingredient_list,
      declaration_letter,
      test_plan: test_plan,
      conformity_certificate,
      conformityCertificateSelection:
        complianceData?.conformityCertificateSelection ||
        "conformity_certificate_not_required",
      ...complianceData,
    };

    setInitialValues(values);
    await form.setFieldsValue({ ...values });
    setReady(true);
  };

  const handleEditProduct = async (values: Record<string, any>) => {
    try {
      await editProductMutation(
        _.pick(values, ["categoryId", "subcategoryId", "productGroupId"])
      );
      productReload();
      setEditProductModal({ ...editProductModal, open: false });
    } catch (error) {
      notification.error((error as any).message);
    }
  };
  useEffect(() => {
    if (complianceData) {
      handleNewData();
      setReadOnly(
        complianceData.task.status === "completed" ||
          complianceData.task.status === "rejected" ||
          complianceData.task.status === "pending_e_signature"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceData]);

  useEffect(() => {
    if (productDetails) {
      if (
        productDetails.status !== "missing_documents" &&
        productDetails.status !== "rejected"
      ) {
        //Will Remove this effect.
        // handleNewData();
      }
    }
  }, [productDetails]);

  React.useEffect(() => {
    if (productDetails && complianceData) {
      handleNewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetails, complianceData]);

  const onSubmit = async (
    isDraft: boolean,
    expiryDate: string = "",
    status = "waiting_for_review",
    reason = []
  ) => {
    const formData = form.getFieldsValue();

    let tempFile: any[] = [];

    if (initialValues?.e_signature) {
      tempFile.push(
        ...initialValues?.e_signature.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType || item.type,
          fileName: item.name || item.fileName,
          size: item.size,
        }))
      );
    }

    if (initialValues?.product_image) {
      tempFile.push(
        ...initialValues?.productImages.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType || item.type,
          fileName: item.name || item.fileName,
          size: item.size,
        }))
      );
    }

    if (initialValues?.packaging_image) {
      tempFile.push(
        ...initialValues?.packagingImages.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (initialValues?.test_report) {
      tempFile.push(
        ...initialValues?.testReport.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (initialValues?.circuit_diagram) {
      tempFile.push(
        ...initialValues?.circuitDiagram.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (initialValues?.instruction_manual) {
      tempFile.push(
        ...initialValues?.instructionManual.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (initialValues?.safety_data_sheet) {
      tempFile.push(
        ...initialValues?.safetyDataSheet.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (initialValues?.ingredient_list) {
      tempFile.push(
        ...initialValues?.ingredientList.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (initialValues?.declaration_letter) {
      tempFile.push(
        ...initialValues?.declarationLetter.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (initialValues?.conformity_certificate) {
      tempFile.push(
        ...initialValues?.conformityCertificate.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (formData?.test_plan) {
      tempFile.push(
        ...formData?.test_plan.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }
    for (let i = 0; i < tempFile.length; i++) {
      if (!tempFile[i].uuid) {
        const hide = message.loading(
          "Some files are still uploading, please wait..",
          0
        );
        // Dismiss manually and asynchronously
        setTimeout(hide, 2500);

        return;
      }
    }

    const submitData = {
      files: tempFile.filter(({ uuid }) => uuid !== -1),
      testPlan: formData?.testPlan,
      status,
      isDrafted: isDraft,
      is_esign_flow: false,
      testLines: {
        ...formData?.testLines,
        cpcTestLines: (
          formData?.testLines?.cpcTestLines ||
          initialValues?.testLines?.cpcTestLines ||
          []
        ).map(
          ({ country, citation, testItem, legislation, id, ...rest }: any) => {
            const additional = id.includes("new")
              ? {}
              : {
                  id,
                };
            return {
              ...rest,
              ...additional,
              citation: { name: citation },
              testItem: { name: testItem },
            };
          }
        ),
        docTestLines: (
          formData?.testLines?.docTestLines ||
          initialValues?.testLines?.docTestLines ||
          []
        ).map(
          ({ country, citation, testItem, legislation, id, ...rest }: any) => {
            const additional = id.includes("new")
              ? {}
              : {
                  id,
                };
            return {
              ...rest,
              citation: { name: citation },
              testItem: { name: testItem },
              legislation: { name: legislation },
            };
          }
        ),
        reportDataTestLines: (
          formData?.testLines?.reportDataTestLines ||
          initialValues?.testLines?.reportDataTestLines ||
          []
        ).map(
          ({ country, citation, testItem, legislation, id, ...rest }: any) => {
            const additional = id.includes("new")
              ? {}
              : {
                  id,
                };
            return {
              ...rest,
              ...additional,
              citation: { name: citation },
              testItem: { name: testItem },
            };
          }
        ),
      },
      expiryDate: status === "completed" ? expiryDate : null,
      conformityCertificateSelection: formData?.conformityCertificateSelection,
      reviewer: {
        ...formData?.reviewer,
      },
      rejectReasons: status === "rejected" ? reason : null,
      conformityCertificateType: formData?.conformityCertificateType,
    };

    await mutate({ params: submitData, taskId: taskId || "" });
  };

  const saveDraft = async () => {
    if (isUpdateLoading) return;
    await onSubmit(true);
    notification.success({ message: <>Saved</> });
  };
  const submitWithPassed = async (expiryDate: any) => {
    await onSubmit(false, expiryDate, "completed");
    setVisibleComplianceExpiryDate(false);
    navigate(`/review/${id}`);
  };
  const submitWithRejected = async (reason: Array<string>) => {
    await onSubmit(false, "", "rejected", reason as any);
    setVisibleComplianceReject(false);
    navigate(`/review/${id}`);
  };

  const onSubmitTestLine = (values: any) => {
    if (!addTestLineFormRef.current) return;

    const rawTestLines = form.getFieldValue([
      "testLines",
      TEST_LINE_FIELDS[typeFormTestLine.current],
    ]);
    let testLines = rawTestLines || [];
    if (testLine.index > -1) {
      testLines.splice(testLine.index, 1, {
        ...values,
        citation: values.citation?.value || values.citation,
        country: values.country?.value,
        testItem: values.testItem?.value || values.testItem,
        legislation: values.legislation?.value,
      });
    } else {
      testLines = testLines.concat([
        {
          ...values,
          citation: values.citation?.value || values.citation,
          country: values.country?.value,
          testItem: values.testItem?.value || values.testItem,
          legislation: values.legislation?.value,
        },
      ]);
    }
    const _testLine = form.getFieldValue(["testLines"]);

    form.setFieldsValue({
      ...form.getFieldsValue(),
      testLines: {
        ..._testLine,
        [TEST_LINE_FIELDS[typeFormTestLine.current]]: testLines,
      },
    });
    addTestLineFormRef.current?.form?.resetFields();

    setVisibleModalTestLine(false);
    setTestLine({ data: {}, index: -1 });
  };

  const onRemoveTestLines = (
    removeId: number | string,
    conformityType: string
  ) => {
    typeFormTestLine.current = conformityType || "DOC";
    const rawTestLines = form.getFieldValue([
      "testLines",
      TEST_LINE_FIELDS[typeFormTestLine.current],
    ]);
    const testLines = rawTestLines || [];
    const removeIndex = testLines.findIndex(({ id }) => id === removeId);
    testLines.splice(removeIndex, 1);
    const _testLine = form.getFieldValue(["testLines"]);
    form.setFieldsValue({
      ...form.getFieldsValue(),
      testLines: {
        ..._testLine,
        [TEST_LINE_FIELDS[typeFormTestLine.current]]: testLines,
      },
    });
  };
  const productDetailInitialValues = React.useMemo(() => {
    return {
      ...productDetails,
      categoryId: (productCategories || []).find(
        ({ name }) => name === productDetails?.category
      )?.id,
      subcategoryId: (subCategories || []).find(
        ({ name }) => name === productDetails?.subcategory
      )?.id,
      productGroupId: (productGroups || []).find(
        ({ name }) => name === productDetails?.productGroup
      )?.id,
      supplierTags: productDetails?.supplierTags?.map((value: any) => ({
        value,
        label: value,
      })),

      exportMarketId: (exportMarkets || []).find(
        ({ name }) => name === productDetails?.exportMarket
      )?.id,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetails, subCategories, productGroups]);

  const onEditTestLine = (
    record: Record<string, any>,
    index: number,
    conformityType: string
  ) => {
    typeFormTestLine.current = conformityType || "DOC";
    const initValues = {
      ...record,
      citation:
        conformityType === "report-data"
          ? record.citation
          : { value: record.citation, label: record.citation },
      country: { value: record.country, label: record.country },
      testItem:
        conformityType === "report-data"
          ? record.testItem
          : { value: record.testItem, label: record.testItem },
      legislation: { value: record.legislation, label: record.legislation },
    };
    typeFormTestLine.current = conformityType || "DOC";
    const rawTestLines = form.getFieldValue([
      "testLines",
      TEST_LINE_FIELDS[typeFormTestLine.current],
    ]);
    let testLines = rawTestLines || [];

    const editIndex = testLines.findIndex(({ id }) => id === record.id);

    setTestLine({
      data: initValues,
      index: editIndex,
    });
    addTestLineFormRef?.current?.form.setFieldsValue(initValues);
    setVisibleModalTestLine(true);
  };

  const onViewCertificate = () => {
    navigate(`/project-page/${id}/${taskId}/doc/${complianceData.id}`);
  };
  const renderConformityFileSection = () => {
    if (!readOnly) return undefined;
    if (complianceData.conformityCertificateSelection === "merchant_uploaded")
      return undefined;

    return () => {
      if (
        complianceData.conformityCertificateSelection ===
        "conformity_certificate_not_required"
      )
        return <>N/A</>;
      return (
        <Button type="primary" ghost onClick={onViewCertificate}>
          View Certificate
        </Button>
      );
    };
  };

  const onHandleEditGeneralInformation = (
    conformityType: string,
    values: any
  ) => {
    const { title, ...data } = values;
    const keyGeneralInformations = GENERATION_FIELDS[conformityType];

    const generalInformations =
      form.getFieldValue(["testLines", keyGeneralInformations]) || [];
    const existed = generalInformations.findIndex(
      ({ title: _title }: { title: string }) => title === _title
    );
    if (existed < 0) {
      generalInformations.push({ ...values, title });
    } else {
      generalInformations[existed] = { ...values, title };
    }
    form.setFieldsValue({
      ...form.getFieldsValue(),
      testLines: {
        ...form.getFieldValue("testLines"),
        [keyGeneralInformations]: generalInformations,
      },
    });
  };
  React.useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const { value } = searchParams.keys().next();

    if (value === "section" && isReady) {
      window.scrollTo({
        top: document.body.scrollHeight,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [search, isReady]);

  return (
    <MainLayout>
      <CusPageHeader
        title={
          <div>
            Compliance Check <Divider type={`vertical`} />{" "}
            <span style={{ color: "#777777" }}>Requirement Detail</span>
          </div>
        }
        onBack={() => {
          if (!readOnly && isEditing.current) {
            showPrevent();
            return;
          }
          navigate(`/review/${id}`);
        }}
      />
      <Page>
        {isReady && (
          <Form
            scrollToFirstError
            name="validate_other"
            form={form}
            layout="vertical"
            onFieldsChange={(changedFields) => {
              if (!isEditing.current) isEditing.current = true;
              const testPlanField = changedFields.find(({ name }) =>
                (name as string[]).includes("test_plan")
              );
              //Will Remove After Demo.
              if (testPlanField) {
                form.setFieldsValue({
                  ...form.getFieldsValue(),
                  test_plan: testPlanField.value.filter(
                    ({ uuid }: any) => uuid !== -1
                  ),
                });
              }

              const conformityStatusField = changedFields.find(({ name }) =>
                (name as string[]).includes("conformityCertificateSelection")
              );

              if (
                conformityStatusField &&
                conformityStatusField.value !==
                  "conformity_certificate_required"
              ) {
                //When user change conformity selection, need to check testLines before changes.
                const testLines = form.getFieldValue("testLines") || [];
                setVisibleConfirmChange(!!testLines.length);
              }
            }}
          >
            <Card>
              <InfoCard>
                <InfoCardHeader>
                  <h2>Product Number: {productDetails?.productNumber}</h2>
                  <Button
                    hidden={productDetails?.status === "completed"}
                    onClick={() => {
                      setEditProductModal({ ...editProductModal, open: true });
                    }}
                    type="link"
                  >
                    Edit
                  </Button>
                </InfoCardHeader>
                {/* <h2>Product Number: {productDetails?.productNumber}</h2> */}
                <div className={"info-card-body"}>
                  <Row wrap={false}>
                    <Col flex="none">
                      <HeadTitle1>Product Name:</HeadTitle1>
                      <Typography.Text>
                        {productDetails?.productName}
                      </Typography.Text>
                      <VerticalLine type={`vertical`} />
                    </Col>
                    <Col flex="none">
                      <HeadTitle1>Age Grade:</HeadTitle1>
                      <Typography.Text>
                        {ageGrade.find(
                          ({ value }) => value === productDetails?.ageGrade
                        )?.label ?? "N/A"}
                      </Typography.Text>
                    </Col>
                  </Row>
                  <Row wrap={false}>
                    <Col flex="none">
                      <HeadTitle1>Export Market: </HeadTitle1>
                      <Typography.Text>
                        {productDetails?.exportMarket}
                      </Typography.Text>
                      <VerticalLine type={`vertical`} />
                    </Col>
                    <Col flex="none">
                      <HeadTitle1>Category: </HeadTitle1>
                      <Typography.Text>
                        {productDetails?.category}
                      </Typography.Text>
                      <VerticalLine type={`vertical`} />
                    </Col>
                    <Col flex="none">
                      <HeadTitle1>Sub-category: </HeadTitle1>
                      <Typography.Text>
                        {productDetails?.subcategory}
                      </Typography.Text>
                      <VerticalLine type={`vertical`} />
                    </Col>
                    <Col flex="none">
                      <HeadTitle1>Service Required: </HeadTitle1>
                      <Typography.Text>
                        {serviceRequired.find(
                          ({ value }) =>
                            value === productDetails?.serviceRequired
                        )?.label ?? "N/A"}
                      </Typography.Text>

                      <VerticalLine type={`vertical`} />
                    </Col>
                    <Col flex="none">
                      <HeadTitle1>ECD: </HeadTitle1>
                      <Typography.Text>
                        {productDetails?.estimatedCompletionDate || "N/A"}
                      </Typography.Text>
                    </Col>
                  </Row>
                  <Row wrap={false}>
                    <Col flex="none">
                      <HeadTitle1>Product Description:</HeadTitle1>
                      <Typography.Text>
                        {productDetails?.productDescription}
                      </Typography.Text>
                    </Col>
                  </Row>
                </div>
              </InfoCard>
              <>
                <InfoCard>
                  <ReviewerUploadDoc
                    title={`Test plan (for staff only)`}
                    required={!readOnly}
                    type={`click`}
                    name={`test_plan`}
                    remarkName={`testPlanRemarks`}
                    uploadDisabled={readOnly}
                    remarkDisabled={readOnly}
                    saveDisabled={readOnly}
                    remarkHidden
                  />
                </InfoCard>
                {ComplianceCheckForm.map((section, index) => {
                  const props: any = { ...section };
                  const conformityProps = section.isConformityCertificate
                    ? {
                        onAddTestLine: (conformityType: string) => {
                          typeFormTestLine.current = conformityType || "DOC";
                          setVisibleModalTestLine(true);
                        },
                        onRemoveTestLine: onRemoveTestLines,
                        onViewCertificate: onViewCertificate,
                        onEditTestLine: onEditTestLine,
                        renderFileSection: renderConformityFileSection(),
                        onEditGeneralInformation: onHandleEditGeneralInformation,
                        onClickGeneratePDF: handleGeneratePDF,
                        product: productDetails,
                        compliance: complianceData,
                      }
                    : {};
                  return (
                    <InfoCard key={`section_${index}`}>
                      <ReviewerComplianceCheck
                        key={`${index}`}
                        readOnly={readOnly}
                        files={form.getFieldValue(section.fileFieldName as any)}
                        remarks={form.getFieldValue(
                          section.remarkFieldName as any
                        )}
                        reUploadName={section.reUploadFieldName}
                        reviewerRemarksName={section.reviewerRemarkFieldName}
                        form={form}
                        {...props}
                        {...conformityProps}
                      ></ReviewerComplianceCheck>
                    </InfoCard>
                  );
                })}
              </>
            </Card>
            {!readOnly && (
              <Row
                justify="space-between"
                align="middle"
                style={{ margin: "40px 0" }}
              >
                <Col flex={"none"}>
                  <Space size={16}>
                    <PassButton
                      size={`large`}
                      type={`primary`}
                      loading={isUpdateLoading}
                      onClick={async () => {
                        try {
                          await form.validateFields();
                          setVisibleComplianceExpiryDate(true);
                        } catch (error) {
                          const { errorFields = [] } = error as any;
                          if (errorFields.length) {
                            window.scrollTo({
                              top: 0,
                              left: 0,
                              behavior: "smooth",
                            });
                          }
                        }
                      }}
                    >
                      Pass
                    </PassButton>
                    <RejectButton
                      size={`large`}
                      danger
                      type="primary"
                      loading={isUpdateLoading}
                      onClick={() => {
                        setVisibleComplianceReject(true);
                      }}
                    >
                      Rejected
                    </RejectButton>
                  </Space>
                </Col>
                <Col flex={"none"}>
                  <SaveDraftButton
                    type="ghost"
                    size={`large`}
                    loading={isUpdateLoading}
                    onClick={() => {
                      saveDraft();
                    }}
                  >
                    Save draft
                  </SaveDraftButton>
                </Col>
              </Row>
            )}
          </Form>
        )}
      </Page>
      {visibleModalTestLine && (
        <SimpleModal
          visible={visibleModalTestLine}
          title={testLine.index > -1 ? "Edit Test Line" : "Add Test Line"}
          onCancel={() => {
            setVisibleModalTestLine(false);
            addTestLineFormRef.current?.form?.resetFields();
            setTestLine({ data: {}, index: -1 });
          }}
          onOk={() => {
            if (!addTestLineFormRef.current) return;
            addTestLineFormRef.current.form.submit();
          }}
        >
          <>
            <CreateTestLine
              testingItemOptions={testItemOptions}
              citationOptions={citationOptions}
              legislationOptions={legislationOptions}
              formType={typeFormTestLine.current}
              ref={addTestLineFormRef}
              onFinish={onSubmitTestLine}
              initialValues={{
                ...testLine.data,
                reportIssueDate: moment(
                  (testLine?.data as any).reportIssueDate
                ),
              }}
            ></CreateTestLine>
          </>
        </SimpleModal>
      )}

      <ModalAlertChangeConformity
        visible={visibleConfirmChange}
        onOk={() => {
          setVisibleConfirmChange(false);
          form.setFieldsValue({
            ...form.getFieldsValue(),
            testLines: [],
          });
        }}
        onCancel={() => {
          //Cancel Change
          form.setFieldsValue({
            ...form.getFieldsValue(),
            conformityCertificateSelection: "conformity_certificate_required",
          });
          setVisibleConfirmChange(false);
        }}
      ></ModalAlertChangeConformity>
      <ModalComplianceExpiryDate
        defaultExpiryDate={complianceData?.task?.expiryDate ?? new Date()}
        onCancel={() => {
          setVisibleComplianceExpiryDate(false);
        }}
        onSubmit={(values: { expiryDate: moment.Moment }) => {
          const { expiryDate } = values;
          submitWithPassed(expiryDate.format("YYYY-MM-DD"));
        }}
        visible={visibleComplianceExpiryDate}
      ></ModalComplianceExpiryDate>
      {visibleComplianceReject && (
        <ModalComplianceReject
          onCancel={() => {
            setVisibleComplianceReject(false);
          }}
          onSubmit={(values: {
            reason: Array<{ value: string; label: string }>;
          }) => {
            const { reason } = values;
            submitWithRejected(reason.map(({ value }) => value));
          }}
          visible={visibleComplianceReject}
        ></ModalComplianceReject>
      )}

      {renderModal()}
      <EditProductModal
        isUpdating={isUpdating}
        open={editProductModal.open}
        initialValues={{ ...productDetailInitialValues }}
        handleCloseModal={() => {
          setEditProductModal({ ...editProductModal, open: false });
        }}
        enabledFields={["categoryId", "subcategoryId", "productGroupId"]}
        updateProduct={handleEditProduct}
      ></EditProductModal>
    </MainLayout>
  );
}

const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  width: 100%;
  margin-bottom: 24px;
  background: #ffffff;
  border: 1px solid rgba(170, 170, 170, 0.3);
  border-radius: 16px;

  .info-card-body {
    color: #777777;
  }
`;
const InfoCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const PassButton = styled(Button)`
  border-color: #005eff;
  background: #005eff;
`;
const RejectButton = styled(Button)`
  // background: #ff5555;
`;

const SaveDraftButton = styled(Button)`
  border: 1px solid #005eff;
  color: #005eff;
`;

const HeadTitle1 = styled.span`
  color: #777777;
  margin-right: 5px;
`;

const VerticalLine = styled(Divider)`
  border: 1px solid #e4e4e4;
  margin: 0px 10px 0px 10px !important;
`;
